import { useCantileverPageHostsOptional } from 'features/knowledgeDocumentViewer/contexts/CantileverPageHostsContext'
import { resolvePageImageFileRefDual } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useEffect, useMemo } from 'react'

export function useRolodexPagePreloader(
  documentId: string,
  page: number,
  enabled = true
): void {
  const pageHosts = useCantileverPageHostsOptional()
  const pageHostId = pageHosts?.getCantileverPageHostId(page)
  const query = useMemo(
    () =>
      enabled
        ? resolvePageImageFileRefDual(documentId, page, 'hiRes', pageHostId)
        : undefined,
    [documentId, enabled, page, pageHostId]
  )

  const { url } = useRolodexSignedURLs(query)

  useEffect(() => {
    if (!url) return

    const img = new Image()
    img.src = url

    return () => {
      img.src = ''
    }
  }, [url])
}

type RolodexPagePreloaderProps = {
  documentId: string
  page: number
}

export function RolodexPagePreloader({
  documentId,
  page,
}: RolodexPagePreloaderProps): React.ReactElement | null {
  useRolodexPagePreloader(documentId, page)
  return null
}

export function computePagesToPreload(
  pageNumber: number,
  totalPages: number,
  showMultiplePages: boolean,
  isContinuous = false
): ReadonlyArray<number> {
  if (totalPages <= 1) return []

  const displayed = new Set([pageNumber])
  if (showMultiplePages && pageNumber < totalPages) {
    displayed.add(pageNumber + 1)
  }

  if (isContinuous) {
    const candidates: number[] = []
    for (let i = -3; i <= 5; i++) {
      const p = pageNumber + i
      if (p >= 1 && p <= totalPages && !displayed.has(p)) {
        candidates.push(p)
      }
    }
    return candidates
  }

  const candidates = showMultiplePages
    ? [
        pageNumber - 2,
        pageNumber - 1,
        pageNumber + 2,
        pageNumber + 3,
        pageNumber + 4,
        pageNumber + 5,
      ]
    : [pageNumber - 1, pageNumber + 1, pageNumber + 2, pageNumber + 3]

  return candidates.filter(
    (p) => p >= 1 && p <= totalPages && !displayed.has(p)
  )
}
